import { ReactNode } from "react";
import { format } from "date-fns";
import {
  BadgeCheckIcon,
  CheckCircle2Icon,
  ChevronRightIcon,
  CircleDotIcon,
  CircleIcon,
  ExternalLinkIcon,
  EyeIcon,
  HelpCircleIcon,
  LinkIcon,
  TriangleAlertIcon,
  UserIcon,
  XCircleIcon,
} from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge, type BadgeProps } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { Campaign, Creator } from "@/types/types";
import { evePhaseKeyForStatus, evePhaseOrder, isEveCampaign, evePayoutOrdinalLabel, pipelineForCreator } from "@/lib/eve";
import { EvePhaseContract, EvePhasePayment, EvePipelinePhase, PhaseArtifact, PostCheck } from "@/types/eve";
import { ScopeWrapper } from "@/components/ui/scope-wrapper";

/**
 * Read-only building blocks for Amy-v2 phase state — shared by the
 * creators-table PhaseDetailDialog and the Tasks-page review/payout views.
 */

export function fmtDate(value?: string | null): string | null {
  if (!value) return null;
  const d = new Date(value);
  if (isNaN(d.getTime())) return null;
  return format(d, "MMM d, yyyy");
}

/**
 * Display name of a phase key. `pipeline` is the RESOLVED phases array the
 * label belongs to — a creator's tier pipeline (pipelineForCreator), since
 * custom labels and payout ordinals differ per tier.
 */
export function phaseDisplayLabel(pipeline: EvePipelinePhase[] | null | undefined, key: string | null): string {
  if (!key) return "Pipeline complete";
  const entry = pipeline?.find((p) => p.key === key);
  if (entry?.label) return entry.label;
  // Split payouts display by ordinal ("Payment 2" for the final of a split).
  const ordinal = evePayoutOrdinalLabel(key, pipeline);
  if (ordinal) return ordinal;
  if (key === "payment_1") return "Payment 1";
  if (key === "payment_2") return "Payment 2";
  return key.charAt(0).toUpperCase() + key.slice(1);
}

/**
 * Segmented pipeline trail: ✓ done / ● current / ○ future, over the phases
 * of the CREATOR'S tier pipeline. Renders nothing when the campaign isn't
 * eve-run, the tier has no enabled phases, or the creator hasn't entered the
 * pipeline yet. Crumbs become buttons when onPhaseClick is given (each opens
 * that phase's detail dialog).
 */
export function PhasePipelineBreadcrumbs({
  campaign,
  creator,
  onPhaseClick,
  className,
}: {
  campaign: Campaign | null | undefined;
  creator: Creator;
  onPhaseClick?: (phaseKey: string) => void;
  className?: string;
}) {
  const status = creator.status ?? "";
  const evePhase = creator.eve_phase;
  const pipeline = pipelineForCreator(campaign, creator);
  const enabled = [...pipeline]
    .filter((p) => p.enabled === true)
    .sort((a, b) => evePhaseOrder(a) - evePhaseOrder(b));
  if (!isEveCampaign(campaign) || enabled.length === 0) return null;

  const currentKey = evePhaseKeyForStatus(status);
  const doneKeys = new Set((evePhase?.history ?? []).map((h) => h.key));
  if (evePhase?.contract?.completed_at) doneKeys.add("contract");
  const allDone = status === "completed";
  if (!allDone && !currentKey && doneKeys.size === 0) return null;

  return (
    <div className={cn("flex flex-wrap items-center gap-1 text-xs", className)}>
      {enabled.map((p, i) => {
        const done = allDone || doneKeys.has(p.key);
        const current = !allDone && currentKey === p.key;
        const crumb = (
          <ScopeWrapper
            className={cn(
              "flex items-center gap-1",
              done ? "text-success" : current ? "font-medium text-foreground" : "text-muted-foreground/60"
            )}
          >
            {done ? (
              <CheckCircle2Icon className="size-3" />
            ) : current ? (
              <CircleDotIcon className="size-3" />
            ) : (
              <CircleIcon className="size-3" />
            )}
            {phaseDisplayLabel(pipeline, p.key)}
          </ScopeWrapper>
        );
        return (
          <div key={p.key} className="flex items-center gap-1">
            {i > 0 && <ChevronRightIcon className="size-3 text-muted-foreground/50" />}
            {onPhaseClick ? (
              <button
                type="button"
                onClick={() => onPhaseClick(p.key)}
                title={`${phaseDisplayLabel(pipeline, p.key)} — view details`}
                className="cursor-pointer rounded-sm px-1 py-0.5 hover:bg-muted"
              >
                {crumb}
              </button>
            ) : (
              crumb
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * One label/value row — the app's divide-y key-value idiom (FramePanel p-0).
 * Display only, so no hover: it must not look selectable.
 */
export function InfoRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 p-4 py-3">
      <span className="text-xs text-muted-foreground shrink-0">{label}</span>
      <span className="text-sm font-medium text-right flex items-center justify-end gap-1.5 min-w-0 flex-wrap">
        {children}
      </span>
    </div>
  );
}

export function ExternalLink({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-1 text-xs underline underline-offset-2 text-muted-foreground hover:text-foreground break-all"
    >
      <LinkIcon className="size-3 shrink-0" />
      {children}
      <ExternalLinkIcon className="size-3 shrink-0" />
    </a>
  );
}

export function PostCheckRow({ check }: { check: PostCheck }) {
  const icon =
    check.live === true ? (
      <CheckCircle2Icon className="size-3.5 text-success shrink-0" />
    ) : check.live === "unverifiable" ? (
      <HelpCircleIcon className="size-3.5 text-muted-foreground shrink-0" />
    ) : (
      <XCircleIcon className="size-3.5 text-destructive shrink-0" />
    );
  return (
    <div className="flex items-center gap-1.5 text-xs">
      {icon}
      <span className="capitalize text-muted-foreground">{check.platform ?? "link"}</span>
      <ExternalLink href={check.canonical || check.url}>{check.canonical || check.url}</ExternalLink>
      {check.author_match === false && (
        <span className="text-destructive">author mismatch</span>
      )}
    </div>
  );
}

const OUTCOME_BADGE: Record<string, BadgeProps["variant"]> = {
  approved: "success",
  revision: "warning",
  pending: "muted",
};

/**
 * One submission as an email-thread style message row — avatar header with
 * the outcome badge and date, the excerpt as the message body (never
 * clamped: the team reviews full scripts here), then attachment-style
 * sub-blocks (assist recommendation, links, media notes, live-post
 * checks). Stack inside a FramePanel p-0 with divide-y — mirrors the
 * Tasks-page review layout.
 */
export function ArtifactThreadItem({
  artifact,
  label,
  username,
  avatar,
}: {
  artifact: PhaseArtifact;
  label: string;
  username?: string;
  avatar?: string;
}) {
  return (
    <div className="flex flex-col p-4">
      <div className="flex flex-row items-center gap-3 w-full">
        <Avatar className="size-10">
          <AvatarFallback className="bg-primary/10 text-primary">
            <UserIcon className="size-5" />
          </AvatarFallback>
          <AvatarImage src={avatar} alt={username} />
        </Avatar>
        <div className="flex flex-col gap-0.5 w-full">
          <p className="font-medium text-sm">@{username}</p>
          <p className="text-xs text-muted-foreground capitalize">{label} submission</p>
        </div>
        <span className="flex items-center gap-2 shrink-0">
          {artifact.outcome && (
            <Badge variant={OUTCOME_BADGE[artifact.outcome] ?? "muted"} size="sm">
              {artifact.outcome}
            </Badge>
          )}
          <span className="text-end text-xs text-muted-foreground text-nowrap">{fmtDate(artifact.at)}</span>
        </span>
      </div>
      {artifact.text_excerpt && (
        <p className="text-sm ml-15 mt-2 text-foreground/90 whitespace-pre-wrap wrap-break-word">
          {/* Runs of blank lines (incl. space-only ones from fetched web pages) render as one. */}
          {artifact.text_excerpt.replace(/\n(?:[^\S\n]*\n){2,}/g, "\n\n")}
        </p>
      )}
      {(artifact.recommendation ||
        (artifact.links?.length ?? 0) > 0 ||
        (artifact.media?.length ?? 0) > 0 ||
        (artifact.post_checks?.length ?? 0) > 0) && (
        <div className="flex flex-col gap-1.5 ml-15 mt-3">
          {artifact.recommendation && (
            <Alert variant={artifact.recommendation.verdict === "approve" ? "success" : "warning"}>
              <AlertTitle>
                {artifact.recommendation.verdict === "approve" ? (
                  <BadgeCheckIcon />
                ) : (
                  <TriangleAlertIcon />
                )}
                {artifact.recommendation.verdict === "approve"
                  ? "Amy recommends approving"
                  : "Amy needs your eyes on this"}
              </AlertTitle>
              {artifact.recommendation.reason && (
                <AlertDescription>{artifact.recommendation.reason}</AlertDescription>
              )}
            </Alert>
          )}
          {artifact.links?.map((link) => (
            <ExternalLink key={link} href={link}>
              {link}
            </ExternalLink>
          ))}
          {artifact.media?.map((m) => (
            <Alert key={m.url} variant={m.watched ? "success" : "warning"}>
              <AlertTitle>
                {m.watched ? <EyeIcon /> : <TriangleAlertIcon />}
                {m.watched ? "Amy watched this file" : "Amy could not watch this file"}
              </AlertTitle>
              {m.notes && <AlertDescription>{m.notes}</AlertDescription>}
            </Alert>
          ))}
          {artifact.post_checks?.map((check) => (
            <PostCheckRow key={check.url} check={check} />
          ))}
        </div>
      )}
    </div>
  );
}

/** Contract facts as a divide-y row list — place inside a FramePanel with p-0. */
export function ContractBlock({ contract }: { contract: EvePhaseContract }) {
  return (
    <div className="flex flex-col divide-y">
      <InfoRow label="Status">
        {contract.completed_at ? (
          <span className="text-success flex items-center gap-1">
            <BadgeCheckIcon className="size-3.5" /> Signed {fmtDate(contract.completed_at)}
          </span>
        ) : contract.declined_at ? (
          <span className="text-destructive">
            Declined {fmtDate(contract.declined_at)}
            {contract.decline_reason ? ` — ${contract.decline_reason}` : ""}
          </span>
        ) : (
          <span className="text-muted-foreground font-normal">Awaiting signature</span>
        )}
      </InfoRow>
      {contract.sign_url && !contract.completed_at && !contract.declined_at && (
        <InfoRow label="Signing link">
          <ExternalLink href={contract.sign_url}>Open</ExternalLink>
        </InfoRow>
      )}
      {contract.document_urls?.length ? (
        <InfoRow label="Documents">
          {contract.document_urls.map((url, i) => (
            <ExternalLink key={url} href={url}>
              Document {contract.document_urls!.length > 1 ? i + 1 : ""}
            </ExternalLink>
          ))}
        </InfoRow>
      ) : null}
      {contract.audit_log_url && (
        <InfoRow label="Audit log">
          <ExternalLink href={contract.audit_log_url}>Open</ExternalLink>
        </InfoRow>
      )}
    </div>
  );
}

/** Payout facts as a divide-y row list — place inside a FramePanel with p-0. */
export function PaymentBlock({ payment }: { payment: EvePhasePayment }) {
  return (
    <div className="flex flex-col divide-y">
      {payment.preview?.amount != null && (
        <InfoRow label="Amount">
          {payment.preview.amount} {payment.preview.currency}
        </InfoRow>
      )}
      {payment.preview?.fee_total != null && <InfoRow label="Fees">{payment.preview.fee_total}</InfoRow>}
      {payment.preview?.net != null && <InfoRow label="Net to creator">{payment.preview.net}</InfoRow>}
      {payment.kyc_status && (
        <InfoRow label="KYC">
          {payment.kyc_status}
          {payment.kyc_invited_at && (
            <span className="text-muted-foreground font-normal text-xs">
              · invited {fmtDate(payment.kyc_invited_at)}
            </span>
          )}
        </InfoRow>
      )}
      {payment.payout_status && (
        <InfoRow label="Status">
          <Badge variant="muted" size="sm" className="capitalize">
            {payment.payout_status}
          </Badge>
        </InfoRow>
      )}
      {payment.pending_expires_at && (
        <InfoRow label="Preview expires">{fmtDate(payment.pending_expires_at)}</InfoRow>
      )}
      {payment.payout_id && (
        <InfoRow label="Payout ID">
          <span className="font-mono text-xs text-muted-foreground">{payment.payout_id}</span>
        </InfoRow>
      )}
      {payment.invoice_id && (
        <InfoRow label="Invoice ID">
          <span className="font-mono text-xs text-muted-foreground">{payment.invoice_id}</span>
        </InfoRow>
      )}
      {payment.payout_link && (
        <InfoRow label="Claim link">
          <ExternalLink href={payment.payout_link}>Open</ExternalLink>
        </InfoRow>
      )}
      {payment.claimed_at && (
        <InfoRow label="Claimed">
          <span className="text-success">{fmtDate(payment.claimed_at)}</span>
        </InfoRow>
      )}
    </div>
  );
}
